// FT-200 (REQ-07, D6): вход в кабинет клиента живёт во вкладке, а не в адресе.
//
// Пара «номер бронирования + фамилия» — единственный ключ к кабинету. В адресе она оседала бы в истории
// браузера, закладках, логах прокси и Referer, поэтому ссылка проката
// `/client_page?booking_id=…&last_name=…&c=<код компании>` входит один раз: consumeQuery забирает пару в
// sessionStorage вкладки и убирает параметры из адреса. sessionStorage — не localStorage: другая вкладка и
// другой человек за тем же компьютером пару не наследуют.
//
// Операция — оплата или привязка карты, начатая из кабинета; за ней следит страница возврата из банка
// (Task 6, /client_page/result). Операция принадлежит паре: смена пары и выход стирают её.
//
// sessionStorage может бросать на любом обращении (SecurityError в приватном режиме старого Safari, запрет
// хранилища сайта, переполнение квоты). Тогда пара и операция живут в памяти страницы: кабинет работает до
// перезагрузки, ничего не падает.
//
// Модуль — часть «набора возврата» (Task 6): не тянет стор, роутер и компоненты кабинета.

const PAIR_KEY = "rp_client_cabinet_pair";
const OPERATION_KEY = "rp_client_cabinet_operation";
const FLOWS = ["payment", "card"];
// Параметры ссылки проката, которые не должны остаться в адресе.
const LINK_PARAMS = ["booking_id", "last_name", "c"];

// Зеркало записей на случай, когда sessionStorage недоступен или отказал в записи.
const memory = {};

function storage() {
  try {
    return window.sessionStorage || null;
  } catch (e) {
    return null;
  }
}

// Сначала память: она зеркалит все записи этой страницы, в том числе те, что sessionStorage отверг. Иначе после
// отказа записи (квота) старое значение в sessionStorage перекрыло бы новое. sessionStorage — когда страница сама
// ничего не писала (после перезагрузки).
function readRaw(key) {
  if (Object.prototype.hasOwnProperty.call(memory, key)) return memory[key];
  const store = storage();
  if (!store) return null;
  try {
    const value = store.getItem(key);
    return value === undefined ? null : value;
  } catch (e) {
    return null;
  }
}

function writeRaw(key, value) {
  memory[key] = value;
  const store = storage();
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch (e) {
    // Квота или запрет — значение осталось в памяти страницы. Прежнее значение стираем, чтобы перезагрузка не
    // воскресила его вместо нового.
    try {
      store.removeItem(key);
    } catch (removeError) {
      // стереть тоже нельзя — после перезагрузки останется прежнее
    }
  }
}

function removeRaw(key) {
  delete memory[key];
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch (e) {
    // нечего стирать
  }
}

function readJson(key) {
  const raw = readRaw(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

function text(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

// Повторённый параметр адреса vue-router отдаёт массивом — берём первое значение.
function queryValue(value) {
  const single = Array.isArray(value) ? value[0] : value;
  const result = text(single);
  return result || null;
}

function normalizePair(pair) {
  if (!pair || typeof pair !== "object") return null;
  const bookingId = text(pair.bookingId);
  const lastName = text(pair.lastName);
  if (!bookingId || !lastName) return null;
  return { bookingId, lastName };
}

function load() {
  return normalizePair(readJson(PAIR_KEY));
}

function finishOperation() {
  removeRaw(OPERATION_KEY);
}

function save(pair) {
  const next = normalizePair(pair);
  if (!next) return null;
  const current = load();
  if (!current || current.bookingId !== next.bookingId || current.lastName !== next.lastName) {
    finishOperation();
  }
  writeRaw(PAIR_KEY, JSON.stringify(next));
  return next;
}

function clear() {
  removeRaw(PAIR_KEY);
  finishOperation();
}

// Ссылка проката → пара во вкладке, параметры — из адреса.
// Возвращает { bookingId, lastName, companyCode } (отсутствующее — null) или null, если параметров ссылки нет:
// тогда адрес не трогается. Неполная пара не сохраняется, но параметры из адреса всё равно убираются.
function consumeQuery(route, router) {
  const query = (route && route.query) || {};
  const present = LINK_PARAMS.some((name) => queryValue(query[name]) !== null);
  if (!present) return null;

  const bookingId = queryValue(query.booking_id);
  const lastName = queryValue(query.last_name);
  const companyCode = queryValue(query.c);

  // Сначала сохранить, потом менять адрес: переход может перерисовать страницу.
  if (bookingId && lastName) save({ bookingId, lastName });

  const rest = {};
  Object.keys(query).forEach((name) => {
    if (LINK_PARAMS.indexOf(name) === -1) rest[name] = query[name];
  });
  if (router && typeof router.replace === "function") {
    try {
      const navigation = router.replace({ path: route.path, query: rest, hash: route.hash });
      if (navigation && typeof navigation.catch === "function") navigation.catch(() => {});
    } catch (e) {
      // адрес не поменялся — пара всё равно во вкладке
    }
  }

  return { bookingId, lastName, companyCode };
}

// Пара для автовхода при открытии кабинета. Бренд на странице входа берётся из кода `c` ссылки, поэтому если
// адрес принёс параметры ссылки (fromLink — результат consumeQuery), входить можно только по паре этой же ссылки:
// пара вкладки могла остаться от бронирования другого проката. Ссылки нет — пара этой вкладки.
function entryPair(fromLink) {
  if (!fromLink) return load();
  return normalizePair(fromLink);
}

// op: { flow: "payment", provider: "stripe" | "yandex_pay", orderId, startedAt }
//   | { flow: "card", paymentId, cardBefore, startedAt }
function startOperation(op) {
  if (!op || FLOWS.indexOf(op.flow) === -1) return null;
  const record = { ...op, startedAt: typeof op.startedAt === "number" ? op.startedAt : Date.now() };
  writeRaw(OPERATION_KEY, JSON.stringify(record));
  return record;
}

function operation() {
  const op = readJson(OPERATION_KEY);
  return op && FLOWS.indexOf(op.flow) !== -1 ? op : null;
}

export default {
  PAIR_KEY,
  OPERATION_KEY,
  save,
  load,
  clear,
  consumeQuery,
  entryPair,
  startOperation,
  operation,
  finishOperation,
};
